// src/pages/CampaignEdit.tsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";

import { isAxiosError } from "axios";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { useTimezone } from "@/contexts/TimezoneContext";
import { normalizeTimezone } from '@/utils/timezoneDetection';
import {
  datetimeLocalToUtcIso,
  formatUtcInTimeZone,
  utcIsoToDatetimeLocalValue,
} from "@/utils/scheduleDateTime";
import { Sidebar } from "@/components/Sidebar";
import { PageHeader } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

interface CampaignForm {
  id?: number;
  campaign_name?: string;
  campaign_subject?: string;
  campaign_type?: string;
  property_title?: string;
  service_type?: string;
  template?: string;
  status?: string;
  scheduled_date?: string;
  schedule_option?: string;
  /** IANA zone used when this launch was scheduled (must drive datetime-local conversions). */
  scheduled_timezone?: string;
  message?: string;
  county_ids?: number[];
  county_details?: { id: number; name: string; agent_count?: number }[];
  custom_dataset_names?: string[];
  custom_agent_list_details?: { id?: number | string; name: string; count?: number }[];
  custom_template?: number | null;
  // Recurring occurrence tracking
  is_recurring_occurrence?: boolean;
  recurring_parent_id?: number;
  occurrence_index?: number;
}

interface CustomTemplateOption {
  id: number;
  name?: string;
  template_name?: string;
  title?: string;
}

const propertyTemplateOptions = Array.from({ length: 11 }, (_, index) => {
  const number = index + 1;
  return { value: `template${number}`, label: `Template ${number}` };
});

const serviceTemplateOptions = Array.from({ length: 8 }, (_, index) => {
  const number = index + 1;
  return { value: `template${number}`, label: `Template ${number}` };
});

const normalizeCampaignType = (value?: string | null): "PROPERTY" | "SERVICE" => {
  const normalized = (value || "property").trim().toLowerCase().replace(/-/g, "_");
  return normalized === "service" ? "SERVICE" : "PROPERTY";
};

const getSavedDatasetLabels = (campaign: CampaignForm): string[] => {
  if (campaign.custom_agent_list_details?.length) {
    return campaign.custom_agent_list_details
      .map((entry) => entry.name?.trim() || (entry.id != null ? String(entry.id) : ""))
      .filter(Boolean);
  }
  return (campaign.custom_dataset_names || []).map(String);
};

const normalizeTemplateKey = (value?: string | null) => {
  const raw = (value || "").trim();
  if (!raw) return "";
  const compact = raw.toLowerCase().replace(/\s+/g, "");
  if (/^\d+$/.test(compact)) return `template${compact}`;
  if (/^template\d+$/.test(compact)) return compact;
  return raw;
};

const getTemplateSelectValue = (campaign: CampaignForm | null) => {
  if (!campaign) return "";
  if (campaign.custom_template) return `custom:${campaign.custom_template}`;
  return normalizeTemplateKey(campaign.template);
};

type RecurringOccurrenceNavState = {
  is_recurring_occurrence?: boolean;
  recurring_parent_id?: number;
  occurrence_index?: number;
  scheduled_date?: string;
};

const CampaignEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const occurrenceNav = (location.state || null) as RecurringOccurrenceNavState | null;
  const { timezone } = useTimezone();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [campaign, setCampaign] = useState<CampaignForm | null>(null);
  const [originalScheduledDate, setOriginalScheduledDate] = useState<string>("");
  const [originalTemplate, setOriginalTemplate] = useState<string>("");
  const [originalSubject, setOriginalSubject] = useState<string>("");
  const [originalMessage, setOriginalMessage] = useState<string>("");
  const [customTemplates, setCustomTemplates] = useState<CustomTemplateOption[]>([]);

  const scheduleTz = React.useMemo(() => {
    const preferred = campaign?.scheduled_timezone || timezone || "UTC";
    return normalizeTimezone(preferred) || "UTC";
  }, [campaign?.scheduled_timezone, timezone]);

  useEffect(() => {
    const fetchCampaign = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);

        const fetchId = occurrenceNav?.recurring_parent_id || id;
        let url = endpoints.getCampaignById(fetchId);
        let resp;

        try {
          resp = await axiosInstance.get(url);
        } catch (e: unknown) {
          if (isAxiosError(e) && e.response?.status === 404) {
            url = `${endpoints.listCampaigns}?property_id=${fetchId}`;
            resp = await axiosInstance.get(url);
          } else {
            throw e;
          }
        }

        const data = resp.data?.campaign || resp.data?.[0] || resp.data;
        if (!data) throw new Error("Empty response");

        const isRecurringOccurrence = Boolean(
          occurrenceNav?.is_recurring_occurrence ?? data.is_recurring_occurrence
        );
        const recurringParentId =
          occurrenceNav?.recurring_parent_id ?? data.recurring_parent_id ?? undefined;
        const occurrenceIndex =
          occurrenceNav?.occurrence_index ?? data.occurrence_index ?? undefined;
        const scheduledDate =
          (isRecurringOccurrence && occurrenceNav?.scheduled_date) ||
          data.scheduled_date ||
          "";

        setCampaign({
          id: data.id,
          campaign_name:
            data.campaign_name || data.property_title || data.service_type || "",
          campaign_subject: data.campaign_subject ?? "",
          campaign_type: normalizeCampaignType(data.campaign_type),
          property_title: data.property_title ?? "",
          service_type: data.service_type ?? "",
          template: data.template ?? "",
          custom_template: data.custom_template ?? null,
          status: data.status ?? "scheduled",
          scheduled_date: scheduledDate,
          schedule_option: data.schedule_option ?? "later",
          scheduled_timezone: normalizeTimezone(data.scheduled_timezone) || timezone || "UTC",
          message: data.message ?? "",
          county_ids: Array.isArray(data.county_ids) ? data.county_ids.map(Number) : [],
          county_details: Array.isArray(data.county_details) ? data.county_details : [],
          custom_dataset_names: Array.isArray(data.custom_dataset_names)
            ? data.custom_dataset_names
            : [],
          custom_agent_list_details: Array.isArray(data.custom_agent_list_details)
            ? data.custom_agent_list_details
            : [],
          is_recurring_occurrence: isRecurringOccurrence,
          recurring_parent_id: recurringParentId,
          occurrence_index: occurrenceIndex,
        });
        setOriginalScheduledDate(scheduledDate);
        setOriginalTemplate(data.template ?? "");
        setOriginalSubject(data.campaign_subject ?? "");
        setOriginalMessage(data.message ?? "");
      } catch (err) {
        console.error("Failed to load Launch:", err);
        setError(true);
        toast.error("Failed to load Launch");
      } finally {
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [
    id,
    occurrenceNav?.is_recurring_occurrence,
    occurrenceNav?.scheduled_date,
    occurrenceNav?.recurring_parent_id,
    occurrenceNav?.occurrence_index,
    timezone,
  ]);

  useEffect(() => {
    const fetchCustomTemplates = async () => {
      try {
        const response = await axiosInstance.get(endpoints.custom_templates);
        const data = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : Array.isArray(response.data?.templates)
              ? response.data.templates
              : [];
        setCustomTemplates(data);
      } catch (err) {
        console.error("Failed to load custom templates:", err);
      }
    };

    fetchCustomTemplates();
  }, []);

  const handleSave = async () => {
    if (!campaign || !id) return;

    setSaving(true);
    try {
      const campaignId = campaign.id || id;
      
      // Check if this is a recurring occurrence and only the scheduled_date changed
      const isRecurringOccurrence = campaign.is_recurring_occurrence;
      const dateChanged = campaign.scheduled_date !== originalScheduledDate;
      const templateChanged = campaign.template !== originalTemplate;
      const subjectChanged = campaign.campaign_subject !== originalSubject;
      const messageChanged = campaign.message !== originalMessage;
      const scheduleTz = campaign.scheduled_timezone || timezone || "UTC";
      
      // Determine if ONLY the date changed (template and other content unchanged)
      const onlyDateChanged = dateChanged && !templateChanged && !subjectChanged && !messageChanged;

      // If editing a specific recurring occurrence and ONLY the date changed,
      // send a targeted update for that occurrence only (prevents duplicates)
      if (isRecurringOccurrence && onlyDateChanged) {
        const payload: Record<string, unknown> = {
          occurrence_index: campaign.occurrence_index,
          recurring_parent_id: campaign.recurring_parent_id,
          old_scheduled_date: originalScheduledDate,
          // Already stored as UTC ISO after datetime-local onChange; do not run localToUtcIso again on Z strings.
          new_scheduled_date: campaign.scheduled_date,
          scheduled_timezone: scheduleTz,
          schedule_option: campaign.schedule_option || "later",
        };

        const response = await axiosInstance.patch(
          endpoints.updateCampaignById(campaign.recurring_parent_id || campaignId),
          payload
        );

        toast.success("Launch occurrence updated successfully!", {
          description: `Scheduled date changed for occurrence ${campaign.occurrence_index}`,
          style: {
            background: "white",
            color: "green",
          },
        });

        navigate("/launches", {
          state: {
            updatedCampaign: response.data,
          },
        });
      } else {
        // Standard full campaign update
        // This handles: template changes, content changes, date changes with other changes, etc.
        const payload: Record<string, unknown> = {
          campaign_name: campaign.campaign_name || "",
          campaign_subject: campaign.campaign_subject || "",
          campaign_type: normalizeCampaignType(campaign.campaign_type),
          property_title: campaign.property_title || "",
          service_type: campaign.service_type || "",
          template: normalizeTemplateKey(campaign.template),
          custom_template_id: campaign.custom_template || null,
          status: campaign.status || "scheduled",
          scheduled_date: campaign.scheduled_date || null,
          scheduled_timezone: scheduleTz,
          schedule_option: campaign.schedule_option || "later",
          message: campaign.message || "",
        };

        const response = await axiosInstance.patch(
          endpoints.updateCampaignById(campaignId),
          payload
        );

        toast.success("Launch updated successfully!", {
          description: "Your changes have been saved.",
          style: {
            background: "white",
            color: "green",
          },
        });

        navigate("/launches", {
          state: {
            updatedCampaign: response.data,
          },
        });
      }
    } catch (err: unknown) {
      console.error("Save failed:", err);
      let msg = "Failed to save launch";
      if (isAxiosError(err)) {
        const d = err.response?.data as { detail?: string; message?: string } | undefined;
        if (d && typeof d === "object") {
          if (typeof d.detail === "string") msg = d.detail;
          else if (typeof d.message === "string") msg = d.message;
          else msg = JSON.stringify(d);
        }
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => navigate("/launches");

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading launch details...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Button variant="outline" onClick={handleCancel}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Launches
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />

      <main className="flex-1 flex flex-col">
        <PageHeader title="Edit Launch" subtitle="Update Launch details and settings" />

        <div className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-7xl space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Launches
              </Button>
              <Button onClick={handleSave} disabled={saving} className="gap-2">
                {saving ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="campaign-name">Launch Name</Label>
                    <Input
                      id="campaign-name"
                      value={campaign.campaign_name || ""}
                      disabled
                      onChange={(e) =>
                        setCampaign((prev) =>
                          prev ? { ...prev, campaign_name: e.target.value } : prev
                        )
                      }
                      placeholder="Enter Launch name..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="campaign-date">Scheduled Date & Time ({scheduleTz}) *</Label>
                    <input
                      id="campaign-date"
                      type="datetime-local"
                      value={utcIsoToDatetimeLocalValue(campaign.scheduled_date, scheduleTz)}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (!value) {
                          setCampaign((prev) =>
                            prev ? { ...prev, scheduled_date: "" } : prev
                          );
                          return;
                        }

                        const utcIso = datetimeLocalToUtcIso(value, scheduleTz);
                        const selectedDate = utcIso ? new Date(utcIso) : null;
                        if (!selectedDate || Number.isNaN(selectedDate.getTime())) {
                          toast.error("Invalid date selected");
                          return;
                        }

                        // Small grace so "now" edge / rounding does not block valid future picks
                        const GRACE_MS = 15_000;
                        if (selectedDate.getTime() < Date.now() - GRACE_MS) {
                          toast.error("You cannot select a past or current time!", {
                            description: `Please choose a future time in ${scheduleTz}.`,
                            duration: 6000,
                          });
                          return;
                        }

                        setCampaign((prev) =>
                          prev ? { ...prev, scheduled_date: utcIso! } : prev
                        );
                      }}
                      step="60"
                      required
                      className="w-full rounded border border-muted/50 px-3 py-2 font-mono font-semibold text-primary"
                    />

                    {campaign.scheduled_date &&
                    !isNaN(new Date(campaign.scheduled_date).getTime()) ? (
                      <div className="mt-2 text-sm text-muted-foreground">
                        <span className="font-semibold text-primary">
                          Launch will run at:{" "}
                          {formatUtcInTimeZone(campaign.scheduled_date, scheduleTz, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            timeZoneName: "short",
                          })}
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="campaign-type">Launch Type</Label>
                    <Select
                      value={normalizeCampaignType(campaign.campaign_type)}
                      onValueChange={(value) =>
                        setCampaign((prev) =>
                          prev ? { ...prev, campaign_type: value } : prev
                        )
                      }
                    >
                      <SelectTrigger id="campaign-type" disabled>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PROPERTY">Property</SelectItem>
                        <SelectItem value="SERVICE">Service</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="campaign-status">Status</Label>
                    <Select
                      value={campaign.status || ""}
                      onValueChange={(value) =>
                        setCampaign((prev) => (prev ? { ...prev, status: value } : prev))
                      }
                    >
                      <SelectTrigger id="campaign-status" disabled>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Launch Content</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject Line</Label>
                  <Input
                    id="subject"
                    value={campaign.campaign_subject || ""}
                    disabled
                    onChange={(e) =>
                      setCampaign((prev) =>
                        prev ? { ...prev, campaign_subject: e.target.value } : prev
                      )
                    }
                    placeholder="Enter email subject"
                  />
                </div>

                {/* <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    rows={6}
                    value={campaign.message || ""}
                    onChange={(e) =>
                      setCampaign((prev) =>
                        prev ? { ...prev, message: e.target.value } : prev
                      )
                    }
                    placeholder="Enter launch message"
                  />
                </div> */}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Property & Template</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {campaign.campaign_type?.toUpperCase() === "SERVICE" ? (
                    <>
                      <Label htmlFor="service">Service Type</Label>
                      <Input
                        id="service"
                        value={campaign.service_type || campaign.campaign_name || ""}
                        disabled
                        onChange={(e) =>
                          setCampaign((prev) =>
                            prev ? { ...prev, service_type: e.target.value } : prev
                          )
                        }
                        placeholder="e.g., Plumbing, Landscaping"
                      />
                    </>
                  ) : (
                    <>
                      <Label htmlFor="property">Property Title</Label>
                      <Input
                        id="property"
                        value={campaign.property_title || ""}
                        disabled
                        onChange={(e) =>
                          setCampaign((prev) =>
                            prev ? { ...prev, property_title: e.target.value } : prev
                          )
                        }
                        placeholder="e.g., Luxury Home in Downtown"
                      />
                    </>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="template">Template</Label>
                  <Select
                    value={getTemplateSelectValue(campaign)}
                    onValueChange={(value) => {
                      setCampaign((prev) => {
                        if (!prev) return prev;
                        if (value.startsWith("custom:")) {
                          return {
                            ...prev,
                            custom_template: Number(value.replace("custom:", "")),
                          };
                        }
                        return {
                          ...prev,
                          template: value,
                          custom_template: null,
                        };
                      });
                    }}
                  >
                    <SelectTrigger id="template" disabled>
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      {(campaign.campaign_type?.toLowerCase() === "service"
                        ? serviceTemplateOptions
                        : propertyTemplateOptions
                      ).map((template) => (
                        <SelectItem key={template.value} value={template.value}>
                          {template.label}
                        </SelectItem>
                      ))}
                      {customTemplates.length > 0 && (
                        <>
                          {customTemplates.map((template) => (
                            <SelectItem key={`custom:${template.id}`} value={`custom:${template.id}`}>
                              {template.name || template.template_name || template.title || `Custom Template ${template.id}`}
                            </SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Target Location</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Launch Saved Hubs</Label>
                  {campaign.county_details && campaign.county_details.length > 0 ? (
                    <div className="space-y-2">
                      {campaign.county_details.map((hub) => (
                        <div
                          key={hub.id}
                          className="flex items-center justify-between rounded-md border p-3"
                        >
                          <span className="font-medium">{hub.name}</span>
                          <span className="text-sm text-muted-foreground">
                            {(hub.agent_count ?? 0).toLocaleString()} agents
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No saved hubs found for this campaign.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Launch Saved Custom Agents</Label>
                  {campaign.custom_agent_list_details &&
                  campaign.custom_agent_list_details.length > 0 ? (
                    <div className="space-y-2">
                      {campaign.custom_agent_list_details.map((list) => (
                        <div
                          key={list.name}
                          className="flex items-center justify-between rounded-md border p-3"
                        >
                          <span className="font-medium">{list.name}</span>
                          <span className="text-sm text-muted-foreground">
                            {(list.count ?? 0).toLocaleString()} contacts
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No saved custom agent lists found for this Launch.
                    </p>
                  )}
                </div>

                {/* {getSavedDatasetLabels(campaign).length > 0 ? (
                  <div className="space-y-2">
                    <Label>Saved Dataset Keys</Label>
                    <div className="flex flex-wrap gap-2">
                      {getSavedDatasetLabels(campaign).map((label) => (
                        <div
                          key={label}
                          className="rounded-md bg-primary/10 px-3 py-1 text-sm"
                        >
                          {label}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null} */}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CampaignEdit;
